import { ProgramContent } from "@domain/program/program";
import {
	getTemplateItemFromTemplate,
	Template,
	TemplateItem,
	TemplateMetadata,
} from "@domain/template/template";
import { TemplateService } from "@domain/template/template-service";
import { Map } from "immutable";
import { observable } from "micro-observables";

export class TemplateManager {
	private readonly _templateById = observable<Map<string, Template>>(Map());
	private readonly _templateItems = observable<TemplateItem[] | undefined>(undefined);

	templateById = this._templateById.readOnly();
	templateItems = this._templateItems.readOnly();

	constructor(private readonly templateService: TemplateService) {}

	async fetchAllTemplates(): Promise<void> {
		const allTemplates = await this.templateService.loadAllTemplates();
		this._templateItems.set(allTemplates);
	}

	async fetchTemplate(templateId: string): Promise<Template> {
		const newTemplate = await this.templateService.loadTemplate(templateId);
		this._templateById.update(templateMap => templateMap.set(templateId, newTemplate));
		return newTemplate;
	}

	async createTemplate(newTemplate: ProgramContent): Promise<TemplateMetadata> {
		const templateMetadata = await this.templateService.createTemplate(newTemplate);

		const newRealTemplate = { id: templateMetadata.id, ...newTemplate };
		this._templateById.update(templateMap => templateMap.set(templateMetadata.id, newRealTemplate));

		this._templateItems.update(templateItems =>
			[...(templateItems ?? []), getTemplateItemFromTemplate(newRealTemplate, templateMetadata.lastEditionDate)].sort(
				(item1, item2) => item2.lastEditionDate.diff(item1.lastEditionDate),
			),
		);
		return templateMetadata;
	}

	async updateTemplate(templateId: string, templateUpdateData: ProgramContent): Promise<void> {
		const templateMetadata = await this.templateService.updateTemplate(templateId, templateUpdateData);

		const newRealTemplate = { id: templateMetadata.id, ...templateUpdateData };
		this._templateById.update(templateMap => templateMap.set(templateMetadata.id, newRealTemplate));

		const updatedTemplateItem = getTemplateItemFromTemplate(newRealTemplate, templateMetadata.lastEditionDate);
		this._templateItems.update(templateItems =>
			templateItems
				?.map(templateItem => (templateItem.id === updatedTemplateItem.id ? updatedTemplateItem : templateItem))
				.sort((item1, item2) => item2.lastEditionDate.diff(item1.lastEditionDate)),
		);
	}

	async transformProgramToTemplate(patientId: string, programId: string, templateName: string): Promise<string> {
		const newTemplate = await this.templateService.transformProgramToTemplate(patientId, programId, templateName);
		this._templateItems.update(templateItems => [newTemplate, ...(templateItems ?? [])]);
		return newTemplate.id;
	}

	async deleteTemplate(templateId: string): Promise<void> {
		await this.templateService.deleteTemplate(templateId);

		this._templateById.update(templateMap => templateMap.delete(templateId));
		this._templateItems.update(templateItems => templateItems?.filter(item => item.id !== templateId));
	}

	async reset(): Promise<void> {
		this._templateById.set(Map());
		this._templateItems.set(undefined);
	}
}
